import React, { useState, useEffect } from 'react';
import { styles, colors } from '../../styles/globalStyles';
import { FichaAluno } from './FichaAluno';

const perfilParaFlags = (perfil) => ({
  is_admin: perfil === 'admin',
  is_professor: perfil === 'professor',
});

const flagsParaPerfil = (u) => {
  if (u.is_admin) return 'admin';
  if (u.is_professor) return 'professor';
  return 'aluno';
};

const PERFIS = [
  { value: 'aluno',     label: 'Aluno',          desc: 'Agendamentos e perfil pessoal' },
  { value: 'professor', label: 'Professor',       desc: 'Vagas, alunos fixos, publicação e usuários (sem financeiro)' },
  { value: 'admin',     label: 'Administrador',   desc: 'Acesso total — incluindo financeiro' },
];

const modalOverlay = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  zIndex: 1000, padding: '1rem', overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
};

const modalBox = {
  background: '#fff', borderRadius: '12px', padding: '1.5rem',
  width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  margin: 'auto', boxSizing: 'border-box',
};

export const GerenciadorUsuarios = ({ createApi, isMobile = false, user = {} }) => {
  const perfisDisponiveis = user?.is_admin
    ? PERFIS
    : PERFIS.filter(p => p.value !== 'admin');
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [busca, setBusca] = useState('');

  const [novoUsuario, setNovoUsuario] = useState({
    nome: '', email: '', telefone: '', perfil: 'aluno', origem: 'regular',
  });

  const [editando, setEditando] = useState(null);
  const [fichaAluno, setFichaAluno] = useState(null);
  const [modalSenha, setModalSenha] = useState(null); // { id, nome }
  const [novaSenha, setNovaSenha] = useState('');

  const mostrarSucesso = (msg) => {
    setSucesso(msg);
    setTimeout(() => setSucesso(''), 3000);
  };

  const carregarUsuarios = async () => {
    setLoading(true);
    try {
      const api = createApi();
      const response = await api.get(`/admin/usuarios${busca ? `?q=${encodeURIComponent(busca)}` : ''}`);
      setUsuarios(response.data);
    } catch {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const criarUsuario = async () => {
    if (!novoUsuario.nome.trim() || !novoUsuario.telefone.trim()) {
      setError('Nome e telefone são obrigatórios');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const api = createApi();
      const payload = {
        nome: novoUsuario.nome,
        email: novoUsuario.email,
        telefone: novoUsuario.telefone,
        origem: novoUsuario.origem,
        ...perfilParaFlags(novoUsuario.perfil),
      };
      const response = await api.post('/admin/usuarios-local', payload);
      setUsuarios([response.data.user, ...usuarios]);
      setNovoUsuario({ nome: '', email: '', telefone: '', perfil: 'aluno', origem: 'regular' });
      mostrarSucesso('Usuário criado! Email de ativação enviado.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const salvarEdicao = async () => {
    if (!editando.nome.trim() || !editando.telefone?.trim()) {
      setError('Nome e telefone são obrigatórios');
      return;
    }
    setError('');
    try {
      const api = createApi();
      const payload = {
        nome: editando.nome,
        email: editando.email,
        telefone: editando.telefone,
        origem: editando.origem || 'regular',
        ...perfilParaFlags(editando.perfil),
      };
      const response = await api.put(`/admin/usuarios/${editando.id}`, payload);
      setUsuarios(usuarios.map(u => u.id === editando.id ? response.data.user : u));
      setEditando(null);
      mostrarSucesso('Usuário atualizado com sucesso!');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar usuário');
    }
  };

  const excluirUsuario = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      const api = createApi();
      await api.delete(`/admin/usuarios/${id}`);
      setUsuarios(usuarios.filter(u => u.id !== id));
      mostrarSucesso('Usuário excluído.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const abrirEdicao = (usuario) => {
    setError('');
    setEditando({ ...usuario, perfil: flagsParaPerfil(usuario) });
  };

  const reenviarAtivacao = async (usuario) => {
    try {
      const api = createApi();
      const res = await api.post(`/admin/usuarios/${usuario.id}/reenviar-ativacao`);
      mostrarSucesso(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar e-mail');
    }
  };

  const confirmarDefinirSenha = async () => {
    if (!novaSenha || novaSenha.length < 6) { setError('Senha deve ter no mínimo 6 caracteres.'); return; }
    try {
      const api = createApi();
      const res = await api.post(`/admin/usuarios/${modalSenha.id}/definir-senha`, { senha: novaSenha });
      setUsuarios(usuarios.map(u => u.id === modalSenha.id ? { ...u, password_set: true } : u));
      setModalSenha(null);
      setNovaSenha('');
      mostrarSucesso(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao definir senha');
    }
  };

  const bloquearUsuario = async (usuario) => {
    const acao = usuario.is_banir ? 'desbloquear' : 'bloquear';
    if (!window.confirm(`Tem certeza que deseja ${acao} ${usuario.nome}?`)) return;
    try {
      const api = createApi();
      const res = await api.patch(`/admin/usuarios/${usuario.id}/bloquear`);
      setUsuarios(usuarios.map(u => u.id === usuario.id ? { ...u, is_banir: res.data.is_banir } : u));
      mostrarSucesso(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar status');
    }
  };

  const bloquearAgendamento = async (usuario) => {
    const acao = usuario.bloquear_agendamento ? 'remover bloqueio de débito de' : 'bloquear agendamento por débito de';
    if (!window.confirm(`Tem certeza que deseja ${acao} ${usuario.nome}?`)) return;
    try {
      const api = createApi();
      const res = await api.patch(`/admin/usuarios/${usuario.id}/bloquear-agendamento`);
      setUsuarios(usuarios.map(u => u.id === usuario.id ? { ...u, bloquear_agendamento: res.data.bloquear_agendamento } : u));
      mostrarSucesso(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao alterar status');
    }
  };

  useEffect(() => {
    const timer = setTimeout(carregarUsuarios, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const formatarData = (d) => new Date(d).toLocaleDateString('pt-BR');

  const badgePerfil = (u) => {
    if (u.is_admin) return <span style={{ ...styles.badge, ...styles.badgeSuccess, fontSize: '0.7rem' }}>Admin</span>;
    if (u.is_professor) return <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#065f46', fontSize: '0.7rem' }}>Professor</span>;
    return null;
  };

  const badgeOrigem = (u) => {
    if (u.origem === 'wellhub')   return <span style={{ fontSize:'0.65rem', background:'#ede9fe', color:'#7c3aed', borderRadius:'4px', padding:'1px 6px', fontWeight:700 }}>WELLHUB</span>;
    if (u.origem === 'totalpass') return <span style={{ fontSize:'0.65rem', background:'#d1fae5', color:'#065f46', borderRadius:'4px', padding:'1px 6px', fontWeight:700 }}>TOTALPASS</span>;
    if (u.origem === 'Fixo')      return <span style={{ fontSize:'0.65rem', background:'#e0f2fe', color:'#0369a1', borderRadius:'4px', padding:'1px 6px', fontWeight:700 }}>⭐ FIXO</span>;
    return null;
  };

  return (
    <div>
      <h3 style={{ marginBottom: '1.5rem', color: colors.dark }}>Gerenciar Usuários</h3>

      {error && (
        <div style={{ ...styles.alert, ...styles.alertError, marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
        </div>
      )}
      {sucesso && (
        <div style={{ ...styles.alert, ...styles.alertSuccess, marginBottom: '1rem' }}>
          {sucesso}
        </div>
      )}

      {/* ── Formulário de criação ── */}
      <div style={{ ...styles.card, marginBottom: '2rem' }}>
        <h4 style={{ marginBottom: '1rem', color: colors.dark }}>Novo Usuário (Login por Email)</h4>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Nome *</label>
            <input
              type="text"
              value={novoUsuario.nome}
              onChange={(e) => setNovoUsuario({ ...novoUsuario, nome: e.target.value })}
              style={styles.formInput}
              placeholder="Nome completo"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Email</label>
            <input
              type="email"
              value={novoUsuario.email}
              onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })}
              style={styles.formInput}
              placeholder="email@exemplo.com"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Telefone *</label>
            <input
              type="tel"
              value={novoUsuario.telefone}
              onChange={(e) => setNovoUsuario({ ...novoUsuario, telefone: e.target.value })}
              style={styles.formInput}
              placeholder="(11) 99999-9999"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Origem</label>
            <select
              value={novoUsuario.origem}
              onChange={(e) => setNovoUsuario({ ...novoUsuario, origem: e.target.value })}
              style={{ ...styles.formInput, cursor: 'pointer' }}
            >
              <option value="regular">Regular</option>
              <option value="Fixo">⭐ Fixo</option>
              <option value="wellhub">Wellhub</option>
              <option value="totalpass">Total Pass</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Perfil</label>
            <select
              value={novoUsuario.perfil}
              onChange={(e) => setNovoUsuario({ ...novoUsuario, perfil: e.target.value })}
              style={{ ...styles.formInput, cursor: 'pointer' }}
            >
              {perfisDisponiveis.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <span style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.25rem', display: 'block' }}>
              {PERFIS.find(p => p.value === novoUsuario.perfil)?.desc}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={criarUsuario}
            disabled={loading || !novoUsuario.nome.trim() || !novoUsuario.telefone.trim()}
            style={{
              ...styles.button, ...styles.buttonPrimary,
              opacity: loading || !novoUsuario.nome.trim() || !novoUsuario.telefone.trim() ? 0.7 : 1,
              cursor: loading || !novoUsuario.nome.trim() || !novoUsuario.telefone.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Criando...' : 'Criar Usuário'}
          </button>
        </div>

        {novoUsuario.email.trim() && (
          <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f0f9ff', borderRadius: '0.5rem', fontSize: '0.875rem' }}>
            <strong>ℹ️</strong> Um email será enviado com link para ativar a conta e criar uma senha.
          </div>
        )}
      </div>

      {/* ── Lista de usuários ── */}
      <div style={{ ...styles.card, marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h4 style={{ margin: 0, color: colors.dark }}>Usuários Cadastrados</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                style={{ ...styles.formInput, paddingLeft: '2.5rem', width: isMobile ? '100%' : '250px', minWidth: '120px' }}
                placeholder="Buscar..."
              />
              <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: colors.gray }}>🔍</span>
            </div>
            <button
              onClick={carregarUsuarios}
              disabled={loading}
              style={{ ...styles.button, ...styles.buttonOutline, ...styles.buttonSmall }}
            >
              {loading ? '...' : '↻'}
            </button>
          </div>
        </div>

        {loading && usuarios.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={styles.spinner}></div>
            <p>Carregando...</p>
          </div>
        ) : usuarios.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray }}>
            {busca ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
          </div>
        ) : isMobile ? (
          /* Cards mobile */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {usuarios.map(usuario => (
              <div key={usuario.id} style={{
                background: usuario.is_banir ? '#fff1f2' : (usuario.origem === 'Fixo' ? '#f0f9ff' : '#f8fafc'),
                border: `1px solid ${usuario.is_banir ? '#fecdd3' : (usuario.origem === 'Fixo' ? '#bae6fd' : '#e2e8f0')}`,
                borderRadius: '10px', padding: '0.875rem',
                borderLeft: usuario.origem === 'Fixo' ? '3px solid #0369a1' : undefined,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1e293b', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                      {usuario.nome}
                      {badgeOrigem(usuario)}
                      {usuario.is_banir && <span style={{ fontSize:'0.65rem', background:'#fee2e2', color:'#dc2626', borderRadius:'4px', padding:'1px 5px', fontWeight:700 }}>BLOQUEADO</span>}
                      {usuario.bloquear_agendamento && <span style={{ fontSize:'0.65rem', background:'#fff7ed', color:'#c2410c', borderRadius:'4px', padding:'1px 5px', fontWeight:700 }}>DÉBITO</span>}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: colors.gray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{usuario.email}</div>
                    {usuario.telefone && <div style={{ fontSize: '0.75rem', color: colors.gray }}>{usuario.telefone}</div>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-end', marginLeft: '8px' }}>
                    <span style={{ ...styles.badge, ...(usuario.auth_type === 'google' ? styles.badgeInfo : styles.badgeWarning), fontSize: '0.7rem' }}>
                      {usuario.auth_type || 'local'}
                    </span>
                    {badgePerfil(usuario)}
                    <span style={{ ...styles.badge, ...((usuario.auth_type === 'google' || usuario.password_set) ? styles.badgeSuccess : styles.badgeDanger), fontSize: '0.7rem' }}>
                      {(usuario.auth_type === 'google' || usuario.password_set) ? 'Ativo' : 'Pendente'}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap:'wrap' }}>
                  <button onClick={() => abrirEdicao(usuario)} style={{ ...styles.button, ...styles.buttonPrimary, ...styles.buttonSmall, flex: 1, fontSize: '0.75rem' }}>✏ Editar</button>
                  <button onClick={() => setFichaAluno(usuario)} style={{ ...styles.button, backgroundColor: '#8b5cf6', color: '#fff', border: 'none', ...styles.buttonSmall, flex: 1, fontSize: '0.75rem' }}>Ficha</button>
                  {usuario.auth_type === 'local' && (
                    <button onClick={() => { setModalSenha(usuario); setNovaSenha(''); }} style={{ ...styles.button, backgroundColor: '#6366f1', color: '#fff', border: 'none', ...styles.buttonSmall, fontSize: '0.75rem' }}
                      title="Definir senha diretamente">
                      🔐 Senha
                    </button>
                  )}
                  {usuario.auth_type === 'local' && usuario.email && (
                    <button onClick={() => reenviarAtivacao(usuario)} style={{ ...styles.button, backgroundColor: usuario.password_set ? '#f59e0b' : '#3b82f6', color: '#fff', border: 'none', ...styles.buttonSmall, fontSize: '0.75rem' }}
                      title={usuario.password_set ? 'Enviar link para redefinir a senha' : 'Enviar link de ativação de conta'}>
                      {usuario.password_set ? '🔑 Resetar' : '📧 Ativar'}
                    </button>
                  )}
                  <button onClick={() => bloquearUsuario(usuario)} style={{ ...styles.button, backgroundColor: usuario.is_banir ? '#10b981' : '#f59e0b', color: '#fff', border: 'none', ...styles.buttonSmall, fontSize: '0.75rem' }}>
                    {usuario.is_banir ? '🔓 Desbloquear' : '🔒 Bloquear'}
                  </button>
                  <button onClick={() => bloquearAgendamento(usuario)} style={{ ...styles.button, backgroundColor: usuario.bloquear_agendamento ? '#10b981' : '#ea580c', color: '#fff', border: 'none', ...styles.buttonSmall, fontSize: '0.75rem' }} title={usuario.bloquear_agendamento ? 'Remover bloqueio por débito' : 'Bloquear agendamento por débito'}>
                    {usuario.bloquear_agendamento ? '💳 Liberar' : '💳 Débito'}
                  </button>
                  <button onClick={() => excluirUsuario(usuario.id)} style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall, fontSize: '0.75rem' }}>Excluir</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Tabela desktop */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.grayLight }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Usuário</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Perfil / Auth</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Cadastro</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: `1px solid ${colors.gray}` }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map(usuario => (
                  <tr key={usuario.id} style={{
                    borderBottom: `1px solid ${colors.grayLight}`,
                    background: usuario.origem === 'Fixo' ? '#f0f9ff' : 'white',
                    borderLeft: usuario.origem === 'Fixo' ? '3px solid #0369a1' : '3px solid transparent',
                  }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '600', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                        {usuario.nome}
                        {badgeOrigem(usuario)}
                        {usuario.is_banir && <span style={{ fontSize:'0.65rem', background:'#fee2e2', color:'#dc2626', borderRadius:'4px', padding:'1px 5px', fontWeight:700 }}>BLOQUEADO</span>}
                        {usuario.bloquear_agendamento && <span style={{ fontSize:'0.65rem', background:'#fff7ed', color:'#c2410c', borderRadius:'4px', padding:'1px 5px', fontWeight:700 }}>DÉBITO</span>}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: colors.gray }}>{usuario.email}</div>
                      {usuario.telefone && <div style={{ fontSize: '0.75rem', color: colors.gray }}>{usuario.telefone}</div>}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <span style={{ ...styles.badge, ...(usuario.auth_type === 'google' ? styles.badgeInfo : styles.badgeWarning), fontSize: '0.75rem', textTransform: 'capitalize' }}>
                          {usuario.auth_type || 'local'}
                        </span>
                        {badgePerfil(usuario)}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{ ...styles.badge, ...((usuario.auth_type === 'google' || usuario.password_set) ? styles.badgeSuccess : styles.badgeDanger), fontSize: '0.75rem' }}>
                        {(usuario.auth_type === 'google' || usuario.password_set) ? 'Ativo' : 'Pendente'}
                      </span>
                      {!usuario.password_set && usuario.auth_type !== 'google' && <div style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.25rem' }}>Aguardando ativação</div>}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.gray }}>{formatarData(usuario.created_at)}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap:'wrap' }}>
                        <button onClick={() => abrirEdicao(usuario)} style={{ ...styles.button, ...styles.buttonPrimary, ...styles.buttonSmall }}>✏ Editar</button>
                        <button onClick={() => setFichaAluno(usuario)} style={{ ...styles.button, backgroundColor: '#8b5cf6', color: '#fff', border: 'none', ...styles.buttonSmall }}>Ficha</button>
                        {usuario.auth_type === 'local' && usuario.email && (
                          <button onClick={() => reenviarAtivacao(usuario)} style={{ ...styles.button, backgroundColor: usuario.password_set ? '#f59e0b' : '#3b82f6', color: '#fff', border: 'none', ...styles.buttonSmall }}
                            title={usuario.password_set ? 'Enviar link para redefinir a senha' : 'Enviar link de ativação de conta'}>
                            {usuario.password_set ? '🔑 Resetar' : '📧 Gerar senha'}
                          </button>
                        )}
                        <button onClick={() => bloquearUsuario(usuario)} style={{ ...styles.button, backgroundColor: usuario.is_banir ? '#10b981' : '#f59e0b', color: '#fff', border: 'none', ...styles.buttonSmall }} title={usuario.is_banir ? 'Desbloquear acesso' : 'Bloquear acesso ao sistema'}>
                          {usuario.is_banir ? '🔓' : '🔒'}
                        </button>
                        <button onClick={() => bloquearAgendamento(usuario)} style={{ ...styles.button, backgroundColor: usuario.bloquear_agendamento ? '#10b981' : '#ea580c', color: '#fff', border: 'none', ...styles.buttonSmall }} title={usuario.bloquear_agendamento ? 'Remover bloqueio por débito' : 'Bloquear agendamento por débito'}>
                          {usuario.bloquear_agendamento ? '💳✓' : '💳'}
                        </button>
                        <button onClick={() => excluirUsuario(usuario.id)} style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Estatísticas ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <div style={{ ...styles.card, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.primary }}>{usuarios.length}</div>
          <div style={{ fontSize: '0.875rem', color: colors.gray }}>Total</div>
        </div>
        <div style={{ ...styles.card, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.success }}>{usuarios.filter(u => u.password_set).length}</div>
          <div style={{ fontSize: '0.875rem', color: colors.gray }}>Ativos</div>
        </div>
        <div style={{ ...styles.card, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981' }}>{usuarios.filter(u => u.is_professor).length}</div>
          <div style={{ fontSize: '0.875rem', color: colors.gray }}>Professores</div>
        </div>
        <div style={{ ...styles.card, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.info }}>{usuarios.filter(u => u.is_admin).length}</div>
          <div style={{ fontSize: '0.875rem', color: colors.gray }}>Administradores</div>
        </div>
      </div>

      {/* ── Modal de edição ── */}
      {editando && (
        <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) setEditando(null); }}>
          <div style={modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h4 style={{ margin: 0, color: colors.dark }}>Editar Usuário</h4>
              <button
                onClick={() => setEditando(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: colors.gray, lineHeight: 1 }}
              >✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Nome *</label>
                <input
                  type="text"
                  value={editando.nome}
                  onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                  style={styles.formInput}
                  placeholder="Nome completo"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email</label>
                <input
                  type="email"
                  value={editando.email || ''}
                  onChange={(e) => setEditando({ ...editando, email: e.target.value })}
                  style={styles.formInput}
                  placeholder="email@exemplo.com"
                  disabled={editando.auth_type === 'google'}
                />
                {editando.auth_type === 'google' && (
                  <span style={{ fontSize: '0.75rem', color: colors.gray }}>Email vinculado ao Google não pode ser alterado</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Telefone *</label>
                <input
                  type="tel"
                  value={editando.telefone || ''}
                  onChange={(e) => setEditando({ ...editando, telefone: e.target.value })}
                  style={styles.formInput}
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Origem</label>
                <select
                  value={editando.origem || 'regular'}
                  onChange={(e) => setEditando({ ...editando, origem: e.target.value })}
                  style={{ ...styles.formInput, cursor: 'pointer' }}
                >
                  <option value="regular">Regular</option>
                  <option value="Fixo">⭐ Fixo</option>
                  <option value="wellhub">Wellhub</option>
                  <option value="totalpass">Total Pass</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Perfil</label>
                <select
                  value={editando.perfil}
                  onChange={(e) => setEditando({ ...editando, perfil: e.target.value })}
                  style={{ ...styles.formInput, cursor: 'pointer' }}
                >
                  {perfisDisponiveis.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <span style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.25rem', display: 'block' }}>
                  {PERFIS.find(p => p.value === editando.perfil)?.desc}
                </span>
              </div>
            </div>

            {error && (
              <div style={{ ...styles.alert, ...styles.alertError, marginTop: '1rem' }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => { setEditando(null); setError(''); }}
                style={{ ...styles.button, ...styles.buttonOutline }}
              >
                Cancelar
              </button>
              <button
                onClick={salvarEdicao}
                disabled={!editando.nome.trim() || !editando.telefone?.trim()}
                style={{
                  ...styles.button, ...styles.buttonPrimary,
                  opacity: !editando.nome.trim() || !editando.telefone?.trim() ? 0.7 : 1,
                  cursor: !editando.nome.trim() || !editando.telefone?.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {fichaAluno && (
        <FichaAluno
          aluno={fichaAluno}
          createApi={createApi}
          onClose={() => setFichaAluno(null)}
        />
      )}

      {/* ── Modal definir senha ── */}
      {modalSenha && (
        <div style={modalOverlay} onClick={() => { setModalSenha(null); setNovaSenha(''); }}>
          <div style={{ ...modalBox, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem' }}>🔐 Definir senha</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#64748b' }}>
              Aluno: <strong>{modalSenha.nome}</strong>
            </p>
            <input
              type="password"
              placeholder="Nova senha (mín. 6 caracteres)"
              value={novaSenha}
              onChange={e => setNovaSenha(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmarDefinirSenha()}
              autoFocus
              style={{ ...styles.formInput, marginBottom: '1rem' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={confirmarDefinirSenha} style={{ ...styles.button, ...styles.buttonPrimary, flex: 1 }}>
                Salvar senha
              </button>
              <button onClick={() => { setModalSenha(null); setNovaSenha(''); }} style={{ ...styles.button, ...styles.buttonOutline, flex: 1 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
